import { useState, useRef, useEffect } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { Field, Label, Input, Button } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/solid";
import RichTextWrapper, { Props as RteProps } from "../../rte/ExternalApi";
import { ToolbarOptions } from "../../rte/Toolbar";
import { toHtml } from "../../rte/Schema";
import { SelectListItem } from "../../components/input/SelectList";
import ModalForm from "../../components/ModalForm";
import RequiredField from "../../components/RequiredField";
import SimpleListbox, { SimpleOption } from "../../components/input/SimpleListbox";
import KeyValuePair from "../../interfaces/keyValuePair";
import MultiSelect from "../peerReviewGroups/MultiSelect";
import {
	StimulusMaterialEditModel,
	StimulusMaterialSaveModel,
	StimulusMaterialViewModel,
	useAddEditStimulusMaterialMutation,
	useDeleteStimulusMaterialMutation,
	useGetStimulusMaterialQuery,
	useLazyEditStimulusMaterialQuery,
} from "../stimulusMaterial/stimulusMaterialApiSlice";
import { StimulusMaterial } from "../stimulusMaterial/StimulusMaterial";
import StimulusMaterialForm from "../stimulusMaterial/StimulusMaterialForm";
import { SavePromptViewModel } from "./promptsApiSlice";

interface PromptStepProps {
	model: SavePromptViewModel;
	promptId?: number;
	genres: SelectListItem[];
	availableContentAreas: SelectListItem[];
	setModel: (model: SavePromptViewModel) => void;
	setHasError: (hasError: boolean) => void;
	updateNewPromptStimMaterialsModel: (model: StimulusMaterialSaveModel[]) => void;
}

export default function PromptStep(props: PromptStepProps) {
	const { model, promptId, genres, availableContentAreas, setModel, setHasError } = props;
	const readOnly = !model.canEdit && !model.isNew;

	const contentAreasPool = availableContentAreas.map((x) => ({
		key: Number(x.value),
		value: x.text,
	}));

	const initializeContentAreas = () => {
		const selected = availableContentAreas
			.filter((s) => model.contentAreaIds.includes(Number(s.value)))
			.map((s) => ({ key: Number(s.value), value: s.text }));
		return selected;
	};

	const [title, setTitle] = useState(model.name ?? "");
	const [description, setDescription] = useState(model.text ?? "");
	const [genre, setGenre] = useState(model.genreId);
	const [genreOptions, setGenreOptions] = useState<SimpleOption[]>([]);
	const [selectedContentAreas, setSelectedContentAreas] = useState(initializeContentAreas());

	const [openStimMaterialForm, setOpenStimMaterialForm] = useState(false);
	const [stimMaterialErrorMessage, setStimMaterialErrorMessage] = useState("");
	const [stimMaterial, setStimMaterial] = useState<StimulusMaterialSaveModel>();
	const [disableStimMaterialConfirm, setDisableStimMaterialConfirm] = useState(false);
	const [newPromptStimMaterials, setNewPromptStimMaterials] = useState<
		StimulusMaterialSaveModel[]
	>([]);
	const [viewableStimMaterials, setViewableStimMaterials] = useState<StimulusMaterialViewModel[]>(
		[]
	);
	// for editing stimulus material
	const [editableStimMaterial, setEditableStimMaterial] = useState<StimulusMaterialEditModel>();
	const [editingStimId, setEditingStimId] = useState<number>();

	const getStimulusMaterialResult = useGetStimulusMaterialQuery(promptId ?? skipToken);
	const [callEditStimMaterial] = useLazyEditStimulusMaterialQuery();
	const [addEditStimulusMaterial] = useAddEditStimulusMaterialMutation();
	const [deleteStimulusMaterial] = useDeleteStimulusMaterialMutation();

	const promptEditor = useRef<RichTextWrapper | null>(null);

	const toolbarOptions: ToolbarOptions = {
		position: "bottom",
		textEditing: true,
	};

	const descriptionProps: RteProps = {
		id: "prompt-rte-editor",
		initialHtml: description,
		readonly: false,
		characterLimit: 20000,
		toolbarOptions: toolbarOptions,
		onDocumentChanged: handlePromptDescriptionChange,
	};

	useEffect(() => {
		if (!genres) return;
		const filters = genres.map((t) => {
			return {
				value: t.value,
				label: t.text,
			} as SimpleOption;
		});
		setGenreOptions(filters);
	}, [genres]);

	useEffect(() => {
		setViewableStimMaterials(getStimulusMaterialResult.data ?? []);
	}, [getStimulusMaterialResult]);

	function handleSelectedChange(selected: KeyValuePair<number, string>[]) {
		setSelectedContentAreas(selected);
	}

	function handlePromptDescriptionChange() {
		if (!promptEditor.current) return;
		setDescription(toHtml(promptEditor.current.state.editor));
	}

	useEffect(() => {
		setModel(updateModel());
		if (!title || !description || description === "<p></p>" || genre === 0) {
			setHasError(true);
		} else {
			setHasError(false);
		}
	}, [title, description, genre, selectedContentAreas]);

	function updateModel(): SavePromptViewModel {
		const newModel: SavePromptViewModel = {
			...model,
			name: title,
			text: description,
			genreId: genre,
			contentAreaIds: selectedContentAreas.map((r) => r.key),
		};
		return newModel;
	}

	function updateStimMaterial(stimMaterial: StimulusMaterialSaveModel) {
		setStimMaterial(stimMaterial);
	}

	useEffect(() => {
		props.updateNewPromptStimMaterialsModel(newPromptStimMaterials);
	}, [newPromptStimMaterials]);

	function handleAddStimulusMaterial() {
		if (model.isNew) {
			if (stimMaterial) {
				const newStimMaterials = [...newPromptStimMaterials, stimMaterial];
				setNewPromptStimMaterials(newStimMaterials);
			}

			if (stimMaterial) {
				const newViewableStimMaterials: StimulusMaterialViewModel[] = [
					...viewableStimMaterials,
					{
						id: 0,
						url: stimMaterial.editModel.url,
						label: stimMaterial.editModel.label,
						stimulusMaterialType: stimMaterial.editModel.stimulusMaterialType,
						fileContentType: "",
						signLanguageVideoUrl: "",
						editMode: false,
					},
				];
				setViewableStimMaterials(newViewableStimMaterials);
			}
			resetStimMaterialForm();
		} else {
			if (stimMaterial) {
				addEditStimulusMaterial(stimMaterial)
					.unwrap()
					.then(() => {
						setStimMaterialErrorMessage("");
						resetStimMaterialForm();
					})
					.catch((error) => setStimMaterialErrorMessage(error.data));
			}
		}
	}

	function getEditStimMaterial(stimId: number) {
		if (model.isNew) {
			var stim = newPromptStimMaterials[stimId];
			var editModel = stim.editModel;
			editModel.fileName = stim.files && stim.files.length > 0 ? stim.files[0].name : "";
			setEditableStimMaterial(editModel);
			setOpenStimMaterialForm(true);
			setEditingStimId(stimId);
		} else {
			if (promptId) {
				callEditStimMaterial({ stimulusMaterialId: stimId, promptId: promptId })
					.unwrap()
					.then((payload) => {
						setEditableStimMaterial(payload);
						setOpenStimMaterialForm(true);
					})
					.catch((error) => console.log(error));
			}
		}
	}

	function handleEditStimulusMaterial() {
		if (model.isNew) {
			if (stimMaterial && editingStimId != undefined) {
				const updatedStimMaterials = [...newPromptStimMaterials];
				updatedStimMaterials[editingStimId] = stimMaterial;
				setNewPromptStimMaterials(updatedStimMaterials);

				const updatedViewableStimMaterials = [...viewableStimMaterials];
				updatedViewableStimMaterials[editingStimId] = {
					id: 0,
					url: stimMaterial.editModel.url,
					label: stimMaterial.editModel.label,
					stimulusMaterialType: stimMaterial.editModel.stimulusMaterialType,
					fileContentType: "",
					signLanguageVideoUrl: "",
					editMode: false,
				};
				setViewableStimMaterials(updatedViewableStimMaterials);
			}
			resetStimMaterialForm();
		} else {
			if (stimMaterial) {
				addEditStimulusMaterial(stimMaterial)
					.unwrap()
					.then(() => {
						setStimMaterialErrorMessage("");
						resetStimMaterialForm();
					})
					.catch((error) => setStimMaterialErrorMessage(error.data));
			}
		}
	}

	function handleDeleteStimulus(stimId: number) {
		if (model.isNew) {
			const updatedStimMaterial = [...newPromptStimMaterials];
			updatedStimMaterial.splice(stimId, 1);
			setNewPromptStimMaterials(updatedStimMaterial);

			const updatedViewableStimMaterial = [...viewableStimMaterials];
			updatedViewableStimMaterial.splice(stimId, 1);
			setViewableStimMaterials(updatedViewableStimMaterial);
		} else {
			if (promptId) {
				deleteStimulusMaterial({ stimulusMaterialId: stimId, promptId: promptId })
					.unwrap()
					.catch((error) => console.log(error));
			}
		}
	}

	function handleCancelStimulusMaterial() {
		setStimMaterialErrorMessage("");
		resetStimMaterialForm();
	}

	function resetStimMaterialForm() {
		setOpenStimMaterialForm(false);
		setEditableStimMaterial(undefined);
		setEditingStimId(undefined);
	}

	return (
		<section className="space-y-6">
			<div
				className={`${viewableStimMaterials.length > 0 || !readOnly ? "border-b border-gray-900/10" : ""} flex pb-4`}>
				<div className="w-full md:w-1/4 pr-3">
					<h2 className="leading-7">Prompt</h2>
					<p className="mt-1 text-sm leading-6 text-gray-600">
						{!readOnly && (
							<span>
								<RequiredField /> - Denotes a required field
							</span>
						)}
					</p>
				</div>
				<div
					className={`${readOnly ? "gap-y-6" : "gap-y-4"} flex flex-col w-full md:w-3/4`}>
					<Field className={readOnly ? "flex items-center" : ""}>
						<Label
							as="span"
							className={`${readOnly ? "w-1/6" : ""} block font-medium leading-6`}>
							Title
							{!readOnly && <RequiredField />}
						</Label>
						{readOnly ? (
							<p className="text-gray-600 text-sm w-5/6">{title}</p>
						) : (
							<Input
								id="title"
								name="title"
								type="text"
								spellCheck={false}
								className="mt-2 resize-none w-full h-10 text-sm rounded-sm border-gray-300"
								value={title}
								readOnly={readOnly}
								onChange={(e) => setTitle(e.target.value)}></Input>
						)}
					</Field>
					<Field className={readOnly ? "flex items-start" : ""}>
						<Label
							as="span"
							className={`${readOnly ? "w-1/6" : ""} block font-medium leading-6`}>
							Description
							{!readOnly && <RequiredField />}
						</Label>
						{readOnly ? (
							<p
								className="text-gray-600 text-sm w-5/6"
								dangerouslySetInnerHTML={{
									__html: description,
								}}></p>
						) : (
							<div id="prompt-description-editor" className="mt-2">
								<RichTextWrapper ref={promptEditor} {...descriptionProps} />
							</div>
						)}
					</Field>
					<Field className={readOnly ? "flex items-center" : ""}>
						<Label
							as="span"
							className={`${readOnly ? "w-1/6" : ""} block font-medium leading-6`}>
							Genre
							{!readOnly && <RequiredField />}
						</Label>
						{readOnly ? (
							<p className="text-gray-600 text-sm w-5/6">
								{genreOptions.find((g) => g.value == genre)?.label}
							</p>
						) : (
							<SimpleListbox
								id="genreDropdown"
								options={genreOptions}
								prompt="Pick a genre..."
								value={genre == 0 ? undefined : genre}
								className={
									"w-full focus:ring-1 focus:ring-inset focus:!ring-primary-600 !py-1.5 mt-2"
								}
								onChange={(e) => setGenre(Number(e.target.value))}
							/>
						)}
					</Field>
					{(!readOnly || (model.contentAreas && model.contentAreas.length > 0)) && (
						<Field className={readOnly ? "flex items-center" : ""}>
							<Label
								as="span"
								className={`${readOnly ? "w-1/6" : ""} block font-medium leading-6`}>
								Content Area(s)
							</Label>
							{readOnly ? (
								<p className="text-gray-600 text-sm w-5/6">
									{model.contentAreas?.join(", ")}
								</p>
							) : (
								<MultiSelect
									pool={contentAreasPool}
									selected={selectedContentAreas}
									className="w-full mt-2"
									defaultText="Choose a content area..."
									onSelectedChange={handleSelectedChange}
								/>
							)}
						</Field>
					)}
					{!model.isNew && (
						<Field className={!model.canEdit ? "flex items-center" : ""}>
							<Label as="span" className="w-1/6 block font-medium leading-6">
								Status
							</Label>
							<p
								className={`${!model.canEdit ? "w-5/6 text-gray-600" : "mt-2"} text-sm`}>
								{model.enabled && model.statusDate
									? "Enabled on " +
										new Date(model.statusDate).toLocaleDateString()
									: "Disabled"}
							</p>
						</Field>
					)}
				</div>
			</div>
			{(viewableStimMaterials.length > 0 || !readOnly) && (
				<div className="flex pb-4">
					<div className="w-full md:w-1/4 pr-3">
						<h2 className="leading-7">Stimulus Material</h2>
					</div>
					<div className="w-full md:w-3/4">
						{viewableStimMaterials.length > 0 && (
							<div className={`${!readOnly ? "mb-3" : ""} flex flex-col`}>
								<Field>
									<StimulusMaterial
										stimuli={viewableStimMaterials}
										linkClassName="text-sm"
										hideTitle={true}
										onEdit={!readOnly ? getEditStimMaterial : undefined}
										onDelete={!readOnly ? handleDeleteStimulus : undefined}
									/>
								</Field>
							</div>
						)}
						{!readOnly && (
							<Button
								className="btn btn-primary"
								onClick={() => setOpenStimMaterialForm(true)}>
								<span className="flex items-center">
									<PlusIcon className="size-4 mr-2" /> Add Stimulus Material
								</span>
							</Button>
						)}
						{openStimMaterialForm && (
							<ModalForm
								open={openStimMaterialForm}
								title={
									editableStimMaterial
										? "Edit Stimulus Material"
										: "Add Stimulus Material"
								}
								form={
									<StimulusMaterialForm
										promptId={props.promptId}
										editableStimulusMaterial={editableStimMaterial}
										updateStimMaterialModel={updateStimMaterial}
										disableConfirm={setDisableStimMaterialConfirm}
										errorMessage={stimMaterialErrorMessage}
									/>
								}
								confirmLabel="Save"
								cancelLabel="Cancel"
								disableConfirm={disableStimMaterialConfirm}
								onConfirm={
									editableStimMaterial
										? handleEditStimulusMaterial
										: handleAddStimulusMaterial
								}
								onCancel={handleCancelStimulusMaterial}
							/>
						)}
					</div>
				</div>
			)}
		</section>
	);
}
